class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

    renderContents(parsedState) {
    const parser = new DOMParser();
    const sections = parsedState.sections || {};
    const drawerHTML = sections['cart-drawer'];

    if (!drawerHTML) {
      throw new Error('Cart drawer HTML was not returned.');
    }

    const drawerDocument = parser.parseFromString(
      drawerHTML,
      'text/html'
    );

    const incomingDrawer = drawerDocument.querySelector('#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');

    if (!incomingDrawer || !currentDrawer) {
      throw new Error('Cart drawer markup could not be found.');
    }

    const wasOpen = this.classList.contains('active');

    if (wasOpen) {
      removeTrapFocus();
    }

    // Replace the full drawer contents, including totals,
    // delivery progress and product suggestions.
    currentDrawer.innerHTML = incomingDrawer.innerHTML;

    const incomingRoot = drawerDocument.querySelector('cart-drawer');

    if (incomingRoot) {
      this.classList.toggle(
        'is-empty',
        incomingRoot.classList.contains('is-empty')
      );
    }

    this.productId = parsedState.id;

    // Update the header bubble independently.
    // A missing bubble must not prevent the drawer updating.
    const bubbleHTML = sections['cart-icon-bubble'];

    if (bubbleHTML) {
      const bubbleDocument = parser.parseFromString(
        bubbleHTML,
        'text/html'
      );

      const incomingBubble =
        bubbleDocument.querySelector('.shopify-section');

      const currentBubble =
        document.getElementById('cart-icon-bubble');

      if (incomingBubble && currentBubble) {
        currentBubble.innerHTML = incomingBubble.innerHTML;
      }
    }

    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this)
    );

    if (wasOpen) {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : currentDrawer;

      const focusElement =
        this.querySelector('.drawer__close') ||
        this.querySelector('.drawer__inner');

      if (container && focusElement) {
        trapFocus(container, focusElement);
      }
    } else {
      this.open();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
